import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowDown, Scissors } from "lucide-react";
import Icon from "@/components/Icon";
import { CBT_CYCLE, CBT_SCENARIO } from "@/data/content";

export default function CBTCycle() {
  const [active, setActive] = useState(1); // default: Thought (the leverage point)

  return (
    <div className="grid gap-10 lg:grid-cols-2 lg:items-start">
      {/* The interactive chain */}
      <div className="relative" data-testid="cbt-cycle">
        <div className="mb-5 inline-flex items-start gap-2 rounded-2xl bg-softblue/50 px-4 py-2.5 text-sm text-navy/80">
          <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-teal" />
          <span>{CBT_SCENARIO}</span>
        </div>
        <div className="flex flex-col gap-2">
          {CBT_CYCLE.map((s, i) => {
            const isActive = active === i;
            return (
              <div key={s.label}>
                <motion.button
                  onMouseEnter={() => setActive(i)}
                  onClick={() => setActive(i)}
                  data-testid={`cbt-step-${i}`}
                  className={`group relative flex w-full items-start gap-4 rounded-2xl border p-4 text-left transition-all duration-300 ${
                    isActive
                      ? "border-teal/40 bg-white shadow-premium scale-[1.02]"
                      : "border-navy-100 bg-white/60 hover:bg-white"
                  }`}
                  whileTap={{ scale: 0.99 }}
                >
                  <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-xl transition-colors ${
                    isActive ? "bg-teal text-white" : "bg-softblue/60 text-navy"
                  }`}>
                    <Icon name={s.icon} className="h-6 w-6" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wider text-teal">Step {i + 1}</div>
                    <div className="font-display text-xl text-navy leading-tight">{s.label}</div>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                  </div>
                </motion.button>

                {i < CBT_CYCLE.length - 1 && (
                  <div className="relative flex justify-center py-1">
                    <ArrowDown className="h-4 w-4 text-navy/30" />
                    {i === 0 && (
                      <span className="absolute right-2 top-1/2 hidden -translate-y-1/2 items-center gap-1.5 rounded-full bg-teal/10 px-3 py-1 text-xs font-medium text-teal sm:flex">
                        <Scissors className="h-3 w-3" /> CBT intervenes here
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Explanation panel */}
      <div className="relative lg:sticky lg:top-28">
        <div className="pointer-events-none absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-softblue/50 to-transparent blur-2xl" />
        <div className="relative rounded-3xl bg-navy p-8 sm:p-10 text-white shadow-premium">
          <div className="text-sm uppercase tracking-widest text-teal-light">The CBT Model</div>
          <motion.h3
            key={active}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="mt-3 font-display text-3xl sm:text-4xl tracking-tight"
          >
            {CBT_CYCLE[active].label}
          </motion.h3>
          <motion.p
            key={`d-${active}`}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="mt-4 text-lg text-white/70 leading-relaxed"
          >
            {CBT_CYCLE[active].desc}
          </motion.p>
          <div className="mt-8 rounded-2xl bg-white/5 border border-white/10 p-5">
            <p className="text-white/85 leading-relaxed">
              <span className="font-semibold text-teal-light">Here's the key:</span> you can't always
              control what happens, but you can influence how you interpret and respond to it. A situation
              that would typically upset you may have more than one explanation. By slowing down and checking
              the facts, you can develop a perspective that is balanced, helpful, and accurate — not simply
              positive. Before accepting a painful thought as true, ask yourself:{" "}
              <em className="text-white">"If I'm going to suffer over this, am I sure I'm right?"</em>{" "}
              Changing your interpretation can interrupt the cycle and lead to healthier emotions,
              behaviours, and outcomes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
